import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import * as characterAPI from '../services/api/character.api';
import * as userAPI from '../services/api/user.api';
import type { CreateCharacterRequest, UpdateCharacterRequest } from '../services/api/character.api';

/**
 * 캐릭터 생성 Hook
 */
export function useCreateCharacter() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateCharacterRequest) => characterAPI.createCharacter(data),
    onSuccess: async (character, variables) => {
      // 캐릭터 생성 성공 시 쿼리 캐시 업데이트
      queryClient.setQueryData(['character', 'me'], character);

      // 프로필 입력 완료 (step: 2) -> 3으로 이동? 아니 2는 프로필생성, 3은 레벨테스트?
      // userAPI.updateOnboarding({ step: 2 }) 호출 중.

      try {
        await userAPI.updateOnboarding({ step: 2 });

        // 로컬 캐시 즉시 업데이트 (refetch 대기 없이) + 입력한 정보 반영
        queryClient.setQueryData(['me'], (old: any) => {
          if (!old) return old;
          return {
            ...old,
            onboardingStep: 2,
            // 입력한 신체 정보 반영
            gender: variables.gender,
            birthdate: variables.birthdate,
            height: variables.height,
            weight: variables.weight,
            // bodyFat: variables.bodyFatPercentage (User 모델에 없을 수 있음)
          };
        });
        
        // 확실히 하기 위해 me 쿼리 무효화
        queryClient.invalidateQueries({ queryKey: ['me'] });

      } catch (error) {
        console.error('Failed to update onboarding step:', error);
      }

      // 레벨 테스트로 이동
      navigate('/onboarding/level-test');
    },
  });
}

/**
 * 내 캐릭터 조회 Hook
 */
export function useMyCharacter() {
  return useQuery({
    queryKey: ['character', 'me'],
    queryFn: () => characterAPI.getMyCharacter(),
    staleTime: 5 * 60 * 1000, // 5분
    retry: false,
  });
}

/**
 * 내 캐릭터 수정 Hook
 */
export function useUpdateCharacter() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateCharacterRequest) => characterAPI.updateMyCharacter(data),
    onSuccess: (character) => {
      // 캐릭터 수정 성공 시 쿼리 캐시 업데이트
      queryClient.setQueryData(['character', 'me'], character);
    },
  });
}
